import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { computeWbsIds } from "@/lib/wbsIds";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { PMBOK_GROUPS, STATUSES, PRIORITIES, KNOWLEDGE_AREAS, DEP_TYPES, TRACK_STATUSES, MS_SHAPES } from "@/lib/constants";
import { Trash2, Loader2, BookMarked, ListTodo, Rocket, ShieldAlert, Plus, Users, CheckCircle2, Layers, CalendarDays, Activity, Link2, UserRound, FileText } from "lucide-react";
import SwatchPicker from "@/components/SwatchPicker";

function NamePicker({ names, onPick, testid }) {
  if (!names.length) return null;
  return (
    <Select value="" onValueChange={onPick}>
      <SelectTrigger className="w-9 px-0 justify-center shrink-0" title="Pick from resource registry" data-testid={testid}>
        <Users className="h-3.5 w-3.5 text-muted-foreground" />
      </SelectTrigger>
      <SelectContent>{names.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
    </Select>
  );
}

const blank = {
  title: "", description: "", phase: "Planning", knowledge_area: "",
  status: "todo", priority: "medium", assignee: "", start_date: "", end_date: "", dependencies: [],
  effort_days: "", fixed_cost: 0, is_milestone: false, is_launch: false, is_summary: false, parent_id: "", ms_shape: "", ms_color: "",
  responsible: "", accountable: "", consulted: "", informed: "", percent_complete: null, notes: "",
  track_status: "on_track", linked_risk_id: "", pace_score: "", swimlane: "", sub_swimlane: "",
};

const bizDays = (sIso, eIso) => {
  if (!sIso || !eIso) return null;
  const s = new Date(sIso + "T00:00:00"), e = new Date(eIso + "T00:00:00");
  if (e < s) return null;
  let n = 0;
  for (const d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
    const w = d.getDay();
    if (w !== 0 && w !== 6) n++;
  }
  return n;
};
const addBizDays = (startIso, n) => {
  const d = new Date(startIso + "T00:00:00");
  let c = d.getDay() === 0 || d.getDay() === 6 ? 0 : 1;
  while (c < n) {
    d.setDate(d.getDate() + 1);
    const w = d.getDay();
    if (w !== 0 && w !== 6) c++;
  }
  const p = (x) => String(x).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const SECTIONS = [
  { key: "basics", label: "Basics", icon: FileText },
  { key: "schedule", label: "Schedule", icon: CalendarDays },
  { key: "people", label: "People", icon: UserRound },
  { key: "status", label: "Status & Risk", icon: Activity },
  { key: "deps", label: "Dependencies", icon: Link2 },
];

export default function TaskDialog({ open, onOpenChange, task, allTasks, phases, seed, onSave, onDelete, projectId, onTruedUp }) {
  const [form, setForm] = useState(blank);
  const [section, setSection] = useState("basics");
  const [saving, setSaving] = useState(false);
  const [savingLib, setSavingLib] = useState(false);
  const [risks, setRisks] = useState([]);
  const [newRiskTitle, setNewRiskTitle] = useState("");
  const [newRiskKind, setNewRiskKind] = useState("risk");
  const [creatingRisk, setCreatingRisk] = useState(false);
  const [truingUp, setTruingUp] = useState(false);
  const [registry, setRegistry] = useState([]);
  const phaseOptions = phases && phases.length ? phases : PMBOK_GROUPS;
  const needsRisk = ["at_risk", "jeopardy"].includes(form.track_status);
  const registryNames = registry.map((r) => r.name);
  const appendName = (field, n) => setForm((f) => {
    const cur = (f[field] || "").split(",").map((s) => s.trim()).filter(Boolean);
    if (!cur.includes(n)) cur.push(n);
    return { ...f, [field]: cur.join(", ") };
  });

  useEffect(() => {
    if (open) api.get("/resource-registry").then(({ data }) => setRegistry(data)).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (open && projectId) {
      api.get(`/projects/${projectId}/risks`).then(({ data }) => setRisks(data)).catch(() => {});
    }
  }, [open, projectId]);

  useEffect(() => {
    if (task) setForm({ ...blank, ...task });
    else if (seed) setForm({ ...blank, ...seed, dependencies: [] });
    else setForm(blank);
    setSection("basics");
  }, [task, seed, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const depId = (d) => (typeof d === "string" ? d : d.predecessor_id);
  const depFor = (id) => (form.dependencies || []).find((d) => depId(d) === id);
  const toggleDep = (id) =>
    setForm((f) => {
      const deps = f.dependencies || [];
      const exists = deps.some((d) => depId(d) === id);
      return {
        ...f,
        dependencies: exists
          ? deps.filter((d) => depId(d) !== id)
          : [...deps, { predecessor_id: id, type: "FS", lag: 0 }],
      };
    });
  const updateDep = (id, patch) =>
    setForm((f) => ({
      ...f,
      dependencies: (f.dependencies || []).map((d) =>
        depId(d) === id ? { predecessor_id: id, type: "FS", lag: 0, ...(typeof d === "object" ? d : {}), ...patch } : d
      ),
    }));

  const predecessors = (allTasks || []).filter((t) => t.id !== task?.id);
  const milestoneOptions = (allTasks || []).filter((t) => t.is_milestone && t.id !== task?.id);
  const wbsIds = useMemo(() => computeWbsIds(allTasks || [], phases || []), [allTasks, phases]);
  const [depFilter, setDepFilter] = useState("");
  const [depSort, setDepSort] = useState("wbs");
  const depGroups = useMemo(() => {
    const q = depFilter.trim().toLowerCase();
    const matches = (t) => !q || t.title.toLowerCase().includes(q) || (wbsIds[t.id] || "").toLowerCase().startsWith(q);
    if (depSort === "az") {
      const items = predecessors.filter(matches).sort((a, b) => a.title.localeCompare(b.title));
      return items.length ? [{ label: null, items }] : [];
    }
    const segs = (t) => { const id = wbsIds[t.id] || ""; return id.startsWith("M") ? [1e9, Number(id.slice(1))] : id.split(".").map(Number); };
    const cmp = (a, b) => { const A = segs(a), B = segs(b); for (let i = 0; i < Math.max(A.length, B.length); i++) { const d = (A[i] ?? 0) - (B[i] ?? 0); if (d) return d; } return 0; };
    const groups = (phases || []).map((g, i) => ({ label: `${i + 1} · ${g}`, items: predecessors.filter((t) => t.phase === g && matches(t)).sort(cmp) }));
    const known = new Set(phases || []);
    const other = predecessors.filter((t) => !known.has(t.phase) && matches(t)).sort(cmp);
    if (other.length) groups.push({ label: "No phase", items: other });
    return groups.filter((g) => g.items.length);
  }, [predecessors, depFilter, depSort, wbsIds, phases]);
  const depMatchCount = depGroups.reduce((n, g) => n + g.items.length, 0);
  const depDepth = (t) => { const id = wbsIds[t.id] || ""; return id.startsWith("M") ? 0 : Math.max(0, id.split(".").length - 2); };
  const successors = useMemo(() => (task ? (allTasks || []).filter((t) => t.id !== task.id && (t.dependencies || []).some((d) => depId(d) === task.id)) : []), [allTasks, task]);
  const depCount = (form.dependencies || []).length;

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.title.trim()) {
      setSection("basics");
      toast.error("Give the task a title first");
      return;
    }
    if (needsRisk && !form.linked_risk_id) {
      setSection("status");
      toast.error("Link or create a risk/issue before saving an At Risk / Jeopardy task.");
      return;
    }
    setSaving(true);
    const payload = { ...form };
    if (!payload.start_date) payload.start_date = null;
    if (!payload.end_date) payload.end_date = null;
    if (payload.is_milestone && payload.start_date) payload.end_date = payload.start_date;
    payload.effort_days = payload.effort_days === "" || payload.effort_days == null ? null : Number(payload.effort_days);
    payload.pace_score = payload.pace_score === "" || payload.pace_score == null ? null : Number(payload.pace_score);
    payload.fixed_cost = Number(payload.fixed_cost || 0);
    await onSave(payload, task?.id);
    setSaving(false);
  };

  const saveToLibrary = async () => {
    setSavingLib(true);
    try {
      await api.post("/library", {
        title: form.title || "Untitled", description: form.description, kind: form.is_milestone ? "milestone" : "task",
        phase: form.phase, knowledge_area: form.knowledge_area, priority: form.priority,
        assignee: form.assignee, default_effort_days: form.effort_days === "" ? null : Number(form.effort_days), tags: [],
      });
      toast.success("Saved to library");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
    } finally {
      setSavingLib(false);
    }
  };

  const quickCreateRisk = async () => {
    if (!newRiskTitle.trim() || !projectId) return;
    setCreatingRisk(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/risks`, {
        title: newRiskTitle.trim(), kind: newRiskKind, probability: 3, impact: 3,
        description: form.title ? `Raised from task "${form.title}"` : "",
      });
      setRisks((r) => [data, ...r]);
      set("linked_risk_id", data.id);
      setNewRiskTitle("");
      toast.success(`${newRiskKind === "issue" ? "Issue" : "Risk"} created & linked`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not create risk");
    } finally {
      setCreatingRisk(false);
    }
  };

  const sectionBadge = (key) => {
    if (key === "deps" && depCount > 0) return depCount;
    if (key === "status" && needsRisk && !form.linked_risk_id) return "!";
    return null;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-w-[95vw] p-0 gap-0 flex flex-col max-h-[92vh] overflow-hidden" data-testid="task-dialog">
        <DialogHeader className="px-6 pt-5 pb-0 shrink-0">
          <DialogTitle className="font-display text-xl">{task ? "Edit task" : "New task"}{form.title ? <span className="text-muted-foreground font-normal text-base"> — {form.title.slice(0, 48)}</span> : ""}</DialogTitle>
          <div className="grid grid-cols-3 gap-2 mt-3" data-testid="task-type-toggle">
            <button type="button" onClick={() => setForm((f) => ({ ...f, is_milestone: false, is_summary: false }))} className={`flex items-center justify-center gap-1.5 rounded-lg border-2 px-2 py-1.5 transition-all ${!form.is_milestone && !form.is_summary ? "border-primary bg-primary/5" : "border-border hover:border-foreground/30"}`} data-testid="task-type-task">
              <ListTodo className={`h-4 w-4 shrink-0 ${!form.is_milestone && !form.is_summary ? "text-primary" : "text-muted-foreground"}`} />
              <span className="text-sm font-bold">Task</span>
            </button>
            <button type="button" onClick={() => setForm((f) => ({ ...f, is_milestone: true, is_summary: false }))} className={`flex items-center justify-center gap-1.5 rounded-lg border-2 px-2 py-1.5 transition-all ${form.is_milestone ? "border-primary bg-primary/5" : "border-border hover:border-foreground/30"}`} data-testid="task-type-milestone">
              <span className={`text-base leading-none shrink-0 ${form.is_milestone ? "text-primary" : "text-muted-foreground"}`} style={{ transform: "translateY(-1px)" }}>▼</span>
              <span className="text-sm font-bold">Milestone</span>
            </button>
            <button type="button" onClick={() => setForm((f) => ({ ...f, is_milestone: false, is_summary: true }))} className={`flex items-center justify-center gap-1.5 rounded-lg border-2 px-2 py-1.5 transition-all ${form.is_summary ? "border-primary bg-primary/5" : "border-border hover:border-foreground/30"}`} data-testid="task-type-summary">
              <Layers className={`h-4 w-4 shrink-0 ${form.is_summary ? "text-primary" : "text-muted-foreground"}`} />
              <span className="text-sm font-bold">Summary</span>
            </button>
          </div>
          <div className="flex items-center gap-1 border-b border-border mt-3 -mb-px overflow-x-auto" data-testid="task-section-tabs">
            {SECTIONS.map(({ key, label, icon: Icon }) => {
              const badge = sectionBadge(key);
              return (
                <button key={key} type="button" onClick={() => setSection(key)}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors ${section === key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                  data-testid={`task-tab-${key}`}>
                  <Icon className="h-3.5 w-3.5" /> {label}
                  {badge != null && <span className={`text-[10px] font-bold px-1.5 py-px rounded-full ${badge === "!" ? "bg-amber-500 text-white" : "bg-secondary text-muted-foreground"}`}>{badge}</span>}
                </button>
              );
            })}
          </div>
        </DialogHeader>

        <form id="task-dialog-form" onSubmit={submit} className="flex-1 min-h-0 flex flex-col">
          <div className="flex-1 min-h-[300px] overflow-y-auto px-6 py-4">

            {section === "basics" && (
              <div className="space-y-4" data-testid="task-section-basics">
                <div>
                  <Label htmlFor="t-title">Title</Label>
                  <Input id="t-title" value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="task-title-input" className="mt-1.5" autoFocus />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Process Group / Phase</Label>
                    <Select value={form.phase} onValueChange={(v) => set("phase", v)}>
                      <SelectTrigger className="mt-1.5" data-testid="task-phase-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{phaseOptions.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Knowledge Area</Label>
                    <Select value={form.knowledge_area || "none"} onValueChange={(v) => set("knowledge_area", v === "none" ? "" : v)}>
                      <SelectTrigger className="mt-1.5" data-testid="task-ka-select"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">—</SelectItem>
                        {KNOWLEDGE_AREAS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="t-desc">Description</Label>
                    <Textarea id="t-desc" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="task-description-input" className="mt-1.5" />
                  </div>
                  <div>
                    <Label htmlFor="t-notes">Notes</Label>
                    <Textarea id="t-notes" rows={3} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} placeholder="Internal notes, context, decisions…" data-testid="task-notes-input" className="mt-1.5" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Swimlane <span className="text-[10px] text-muted-foreground font-normal">(workstream)</span></Label>
                    <Input className="mt-1.5" value={form.swimlane} onChange={(e) => set("swimlane", e.target.value)} placeholder="e.g. Engineering" data-testid="task-swimlane-input" />
                  </div>
                  <div>
                    <Label>Sub-swimlane</Label>
                    <Input className="mt-1.5" value={form.sub_swimlane} onChange={(e) => set("sub_swimlane", e.target.value)} placeholder="e.g. Backend" data-testid="task-subswimlane-input" />
                  </div>
                </div>
                {form.is_milestone && (
                  <div className="flex items-center gap-2 flex-wrap border border-border rounded-lg p-2.5" data-testid="milestone-style-row">
                    <span className="text-xs font-semibold text-muted-foreground">Shape</span>
                    {Object.entries(MS_SHAPES).map(([k, g]) => (
                      <button key={k} type="button" onClick={() => set("ms_shape", k)} title={k}
                        className={`h-8 w-8 rounded border text-base leading-none transition-colors ${(form.ms_shape || "triangle") === k ? "border-primary bg-primary/10" : "border-border text-muted-foreground hover:border-foreground/40"}`}
                        style={(form.ms_shape || "triangle") === k ? { color: form.ms_color || "#0044FF" } : undefined}
                        data-testid={`ms-shape-${k}`}>{g}</button>
                    ))}
                    <span className="text-xs font-semibold text-muted-foreground ml-2">Color</span>
                    <SwatchPicker value={form.ms_color || "#0044FF"} onChange={(v) => set("ms_color", v)} title="Milestone color" testid="ms-color-picker" />
                    <span className="text-[10px] text-muted-foreground">shows on the roadmap, timeline & plan</span>
                  </div>
                )}
                <label className="flex items-center gap-2.5 rounded-lg border-2 border-border p-2.5 cursor-pointer hover:border-foreground/30 transition-all" data-testid="task-launch-toggle">
                  <Checkbox checked={!!form.is_launch} onCheckedChange={(v) => set("is_launch", !!v)} data-testid="task-launch-checkbox" />
                  <Rocket className={`h-4 w-4 ${form.is_launch ? "text-primary" : "text-muted-foreground"}`} />
                  <div>
                    <p className="text-sm font-bold leading-tight">Launch / key deliverable</p>
                    <p className="text-[11px] text-muted-foreground">Marked with a ★ on the swimlane roadmap</p>
                  </div>
                </label>
              </div>
            )}

            {section === "schedule" && (
              <div className="space-y-4" data-testid="task-section-schedule">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label htmlFor="t-start">Start date</Label>
                    <Input id="t-start" type="date" value={form.start_date || ""} onChange={(e) => {
                      const v = e.target.value;
                      setForm((f) => {
                        const next = { ...f, start_date: v };
                        if (!f.is_milestone && v && f.start_date && f.end_date) {
                          const d = bizDays(f.start_date, f.end_date);
                          if (d) next.end_date = addBizDays(v, d);
                        }
                        return next;
                      });
                    }} data-testid="task-start-input" className="mt-1.5" />
                  </div>
                  <div>
                    <Label htmlFor="t-end">End date {form.is_milestone && <span className="text-[11px] text-muted-foreground">(0-day)</span>}</Label>
                    <Input id="t-end" type="date" value={form.is_milestone ? (form.start_date || "") : (form.end_date || "")} onChange={(e) => set("end_date", e.target.value)} disabled={form.is_milestone} data-testid="task-end-input" className="mt-1.5" />
                  </div>
                  <div>
                    <Label htmlFor="t-duration">Duration <span className="text-[11px] text-muted-foreground">(work days)</span></Label>
                    <Input id="t-duration" type="number" min="1"
                      value={form.is_milestone ? 0 : (bizDays(form.start_date, form.end_date) ?? "")}
                      onChange={(e) => {
                        const n = parseInt(e.target.value, 10);
                        if (n > 0 && form.start_date && !form.is_milestone) set("end_date", addBizDays(form.start_date, n));
                      }}
                      disabled={form.is_milestone || !form.start_date}
                      placeholder={form.start_date ? "" : "Set a start date first"}
                      title="Changing the duration moves the end date (weekends skipped)"
                      data-testid="task-duration-input" className="mt-1.5" />
                  </div>
                </div>
                {!form.is_milestone && (
                  <div>
                    <Label>Parent milestone <span className="text-[11px] text-muted-foreground">(nest this task under a milestone)</span></Label>
                    <Select value={form.parent_id || "none"} onValueChange={(v) => set("parent_id", v === "none" ? "" : v)}>
                      <SelectTrigger className="mt-1.5" data-testid="task-parent-select"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— None —</SelectItem>
                        {milestoneOptions.map((m) => <SelectItem key={m.id} value={m.id}>◆ {m.title}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label htmlFor="t-effort">Effort (days)</Label>
                    <Input id="t-effort" type="number" min="0" step="0.5" value={form.effort_days ?? ""} onChange={(e) => set("effort_days", e.target.value)} placeholder="Auto from dates" data-testid="task-effort-input" className="mt-1.5" />
                  </div>
                  <div>
                    <Label htmlFor="t-fixed">Fixed cost</Label>
                    <Input id="t-fixed" type="number" min="0" value={form.fixed_cost ?? 0} onChange={(e) => set("fixed_cost", e.target.value)} data-testid="task-fixedcost-input" className="mt-1.5" />
                  </div>
                  <div>
                    <Label htmlFor="t-pace">Pace (hrs/day)</Label>
                    <Input id="t-pace" type="number" min="0" step="0.5" value={form.pace_score ?? ""} onChange={(e) => set("pace_score", e.target.value)} placeholder="Auto" title="Hours per day this task should take — used by Pilot" data-testid="task-pace-input" className="mt-1.5" />
                  </div>
                </div>
              </div>
            )}

            {section === "people" && (
              <div className="space-y-4" data-testid="task-section-people">
                <div>
                  <Label htmlFor="t-assignee">Assignee / Team <span className="text-[11px] text-muted-foreground">(multiple allowed)</span></Label>
                  {!!(form.assignee || "").trim() && (
                    <div className="flex flex-wrap gap-1 mt-1.5" data-testid="task-assignee-chips">
                      {form.assignee.split(",").map((s) => s.trim()).filter(Boolean).map((n) => (
                        <span key={n} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary text-xs font-medium">
                          {n}
                          <button type="button" onClick={() => set("assignee", form.assignee.split(",").map((s) => s.trim()).filter((x) => x && x !== n).join(", "))} className="text-muted-foreground hover:text-red-500" data-testid={`task-assignee-remove-${n.replace(/\s/g, "-")}`}>×</button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="mt-1.5 flex gap-1.5">
                    <Input id="t-assignee" value={form.assignee} onChange={(e) => set("assignee", e.target.value)} placeholder="Names, comma-separated — or add from the registry →" data-testid="task-assignee-input" />
                    <NamePicker names={registryNames} onPick={(n) => appendName("assignee", n)} testid="task-assignee-picker" />
                  </div>
                </div>
                <div className="border border-border rounded-md p-3 space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">RACI assignment</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="t-resp" className="flex items-center gap-1.5"><span className="h-4 w-4 rounded text-white text-[10px] font-bold flex items-center justify-center" style={{ background: "#10B981" }}>R</span> Responsible</Label>
                      <div className="mt-1.5 flex gap-1.5">
                        <Input id="t-resp" value={form.responsible} onChange={(e) => set("responsible", e.target.value)} placeholder="Does the work" data-testid="task-responsible-input" />
                        <NamePicker names={registryNames} onPick={(n) => set("responsible", n)} testid="task-responsible-picker" />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="t-acct" className="flex items-center gap-1.5"><span className="h-4 w-4 rounded text-white text-[10px] font-bold flex items-center justify-center" style={{ background: "#2563EB" }}>A</span> Accountable</Label>
                      <div className="mt-1.5 flex gap-1.5">
                        <Input id="t-acct" value={form.accountable} onChange={(e) => set("accountable", e.target.value)} placeholder="Signs off (one person)" data-testid="task-accountable-input" />
                        <NamePicker names={registryNames} onPick={(n) => set("accountable", n)} testid="task-accountable-picker" />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="t-cons" className="flex items-center gap-1.5"><span className="h-4 w-4 rounded text-white text-[10px] font-bold flex items-center justify-center" style={{ background: "#F59E0B" }}>C</span> Consulted</Label>
                      <div className="mt-1.5 flex gap-1.5">
                        <Input id="t-cons" value={form.consulted} onChange={(e) => set("consulted", e.target.value)} placeholder="Comma-separated" data-testid="task-consulted-input" />
                        <NamePicker names={registryNames} onPick={(n) => appendName("consulted", n)} testid="task-consulted-picker" />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="t-inf" className="flex items-center gap-1.5"><span className="h-4 w-4 rounded text-white text-[10px] font-bold flex items-center justify-center" style={{ background: "#64748b" }}>I</span> Informed</Label>
                      <div className="mt-1.5 flex gap-1.5">
                        <Input id="t-inf" value={form.informed} onChange={(e) => set("informed", e.target.value)} placeholder="Comma-separated" data-testid="task-informed-input" />
                        <NamePicker names={registryNames} onPick={(n) => appendName("informed", n)} testid="task-informed-picker" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {section === "status" && (
              <div className="space-y-4" data-testid="task-section-status">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Status</Label>
                    <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v, percent_complete: v === "done" ? 100 : f.percent_complete }))}>
                      <SelectTrigger className="mt-1.5" data-testid="task-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Priority</Label>
                    <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                      <SelectTrigger className="mt-1.5" data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="t-pct" className="flex items-center justify-between">
                    <span>% complete</span>
                    {form.pm_confirmed
                      ? <span className="text-[11px] font-semibold text-emerald-600">PM confirmed</span>
                      : <span className="text-[11px] font-semibold text-gray-500">not confirmed — stays gray until true-up</span>}
                  </Label>
                  <div className="flex items-center gap-3 mt-1.5">
                    <Input id="t-pct" type="number" min="0" max="100" value={form.percent_complete ?? ""} onChange={(e) => set("percent_complete", e.target.value === "" ? null : Number(e.target.value))} placeholder="0" data-testid="task-percent-input" className="w-24" />
                    {task?.id ? (
                      <Button type="button" size="sm" variant="outline" className="gap-1.5 h-9" disabled={truingUp}
                        onClick={async () => {
                          setTruingUp(true);
                          try {
                            await api.post(`/tasks/${task.id}/confirm-progress`, { percent: Number(form.percent_complete ?? 0) });
                            setForm((f) => ({ ...f, pm_confirmed: true }));
                            toast.success(`Progress confirmed at ${Number(form.percent_complete ?? 0)}%`);
                            onTruedUp?.();
                          } catch (e) { toast.error("Could not confirm progress"); }
                          finally { setTruingUp(false); }
                        }} data-testid="task-trueup-button">
                        {truingUp ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />} True up now
                      </Button>
                    ) : (
                      <span className="text-xs text-muted-foreground">Confirm progress via the True-up screen to turn tasks green/blue.</span>
                    )}
                  </div>
                </div>
                <div>
                  {(() => {
                    if (task?.is_milestone || form.status === "done" || form.linked_risk_id || ["at_risk", "jeopardy"].includes(form.track_status)) return null;
                    const today = new Date().toISOString().slice(0, 10);
                    let level = null;
                    if (form.end_date && form.end_date < today) level = "overdue";
                    else if (form.start_date && form.end_date && form.start_date <= today && form.end_date >= today) {
                      const exp = Math.min(100, Math.round((Date.parse(today) - Date.parse(form.start_date)) / Math.max(1, Date.parse(form.end_date) - Date.parse(form.start_date)) * 100));
                      if (exp - (Number(form.percent_complete) || 0) >= 20) level = "behind";
                    }
                    if (!level) return null;
                    return (
                      <div className="mb-3 flex items-center gap-2.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5" data-testid="task-risk-prompt">
                        <ShieldAlert className="h-4 w-4 text-amber-600 shrink-0" />
                        <p className="text-xs text-amber-800 flex-1">
                          This task is {level === "overdue" ? <b>overdue</b> : <b>tracking behind its expected progress</b>} and has no linked risk.
                        </p>
                        <Button type="button" size="sm" variant="outline" className="h-7 text-xs border-amber-400 text-amber-700 hover:bg-amber-100 shrink-0"
                                onClick={() => set("track_status", "at_risk")} data-testid="task-risk-prompt-flag">
                          Flag At Risk & link a risk
                        </Button>
                      </div>
                    );
                  })()}
                  <Label>Track status</Label>
                  <div className="grid grid-cols-3 gap-2 mt-1.5" data-testid="task-track-toggle">
                    {TRACK_STATUSES.map((ts) => (
                      <button key={ts.key} type="button" onClick={() => set("track_status", ts.key)}
                        className={`rounded-lg border-2 px-2 py-2 text-sm font-semibold transition-all ${form.track_status === ts.key ? "border-current" : "border-border text-muted-foreground hover:border-foreground/30"}`}
                        style={form.track_status === ts.key ? { color: ts.color, background: `${ts.color}14`, borderColor: ts.color } : {}}
                        data-testid={`task-track-${ts.key}`}>
                        {ts.label}
                      </button>
                    ))}
                  </div>
                  {needsRisk && (
                    <div className="mt-3 rounded-lg border-2 p-3 space-y-2.5" style={{ borderColor: "#F59E0B55", background: "#F59E0B0d" }} data-testid="task-risk-link-section">
                      <p className="text-xs font-semibold flex items-center gap-1.5" style={{ color: "#B45309" }}>
                        <ShieldAlert className="h-4 w-4" /> An {form.track_status === "jeopardy" ? "in-Jeopardy" : "At Risk"} task must be linked to a risk or issue.
                      </p>
                      <Select value={form.linked_risk_id || "none"} onValueChange={(v) => set("linked_risk_id", v === "none" ? "" : v)}>
                        <SelectTrigger data-testid="task-linked-risk-select">
                          <SelectValue placeholder="Link an existing risk/issue">
                            {form.linked_risk_id ? (risks.find((r) => r.id === form.linked_risk_id)?.title || "Linked risk") : "— Select a risk or issue —"}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">— Select a risk or issue —</SelectItem>
                          {risks.map((r) => <SelectItem key={r.id} value={r.id} data-testid={`linked-risk-option-${r.id}`}>{r.kind === "issue" ? "⚠" : "◆"} {r.title} (score {r.score})</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <div className="flex items-center gap-2">
                        <Input value={newRiskTitle} onChange={(e) => setNewRiskTitle(e.target.value)} placeholder="…or describe a new risk/issue" className="flex-1" data-testid="task-new-risk-input" />
                        <Select value={newRiskKind} onValueChange={setNewRiskKind}>
                          <SelectTrigger className="w-[92px]" data-testid="task-new-risk-kind"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="risk">Risk</SelectItem>
                            <SelectItem value="issue">Issue</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button type="button" size="sm" variant="outline" className="gap-1 shrink-0" onClick={quickCreateRisk} disabled={creatingRisk || !newRiskTitle.trim()} data-testid="task-create-risk-button">
                          {creatingRisk ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Create & link
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {section === "deps" && (
              <div className="space-y-3" data-testid="task-section-deps">
                <Label>Dependencies (predecessors)</Label>
                <div className="flex items-center gap-2">
                  <Input value={depFilter} onChange={(e) => setDepFilter(e.target.value)} placeholder="Search by task ID (e.g. 2.3) or name…" className="h-8 text-sm flex-1" data-testid="dep-search-input" />
                  <button type="button" onClick={() => setDepSort((s) => (s === "wbs" ? "az" : "wbs"))}
                    className="shrink-0 text-[11px] font-bold px-2.5 py-1.5 rounded-md border border-border hover:bg-secondary text-muted-foreground"
                    title="Toggle sort order" data-testid="dep-sort-toggle">
                    {depSort === "wbs" ? "WBS order" : "A–Z"}
                  </button>
                </div>
                <div className="max-h-64 overflow-y-auto border border-border rounded-md" data-testid="task-dependencies">
                  {predecessors.length === 0 && <p className="text-xs text-muted-foreground px-3 py-2">No other tasks to depend on yet.</p>}
                  {predecessors.length > 0 && depMatchCount === 0 && <p className="text-xs text-muted-foreground px-3 py-2">No tasks match "{depFilter}".</p>}
                  {depGroups.map((g, gi) => (
                    <div key={g.label || "az"}>
                      {g.label && (
                        <div className={`sticky top-0 z-10 px-2 py-1 bg-secondary/80 backdrop-blur-sm border-b border-border ${gi > 0 ? "border-t" : ""}`} data-testid="dep-group-header">
                          <span className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">{g.label}</span>
                        </div>
                      )}
                      <div className="p-2 space-y-1.5">
                        {g.items.map((t) => {
                          const dep = depFor(t.id);
                          return (
                            <div key={t.id} className="rounded hover:bg-secondary/40 px-1 py-1" style={{ marginLeft: depDepth(t) * 16 }}>
                              <label className="flex items-center gap-2 text-sm cursor-pointer">
                                <Checkbox checked={!!dep} onCheckedChange={() => toggleDep(t.id)} data-testid={`dep-option-${t.id}`} />
                                <span className="font-mono text-[11px] font-bold text-muted-foreground w-12 shrink-0 text-right">{wbsIds[t.id] || "—"}</span>
                                <span className="truncate flex-1">{t.title}</span>
                              </label>
                              {dep && (
                                <div className="flex items-center gap-2 pl-6 mt-1.5">
                                  <Select value={dep.type || "FS"} onValueChange={(v) => updateDep(t.id, { type: v })}>
                                    <SelectTrigger className="h-7 w-[150px] text-xs" data-testid={`dep-type-${t.id}`}><SelectValue /></SelectTrigger>
                                    <SelectContent>{DEP_TYPES.map((d) => <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>)}</SelectContent>
                                  </Select>
                                  <Input type="number" value={dep.lag ?? 0} onChange={(e) => updateDep(t.id, { lag: Number(e.target.value) })} className="h-7 w-16 text-xs" data-testid={`dep-lag-${t.id}`} />
                                  <span className="text-[11px] text-muted-foreground">days lag</span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
                {successors.length > 0 && (
                  <div>
                    <Label className="text-xs">Successors — depend on this task</Label>
                    <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="task-successors">
                      {successors.map((s) => {
                        const d = (s.dependencies || []).find((x) => depId(x) === task.id);
                        const typ = (typeof d === "object" && d?.type) || "FS";
                        return (
                          <span key={s.id} className="inline-flex items-center gap-1.5 text-xs bg-secondary rounded-md px-2 py-1" data-testid={`task-successor-${s.id}`}>
                            <span className="font-mono text-[10px] font-bold text-muted-foreground">{wbsIds[s.id] || "—"}</span>
                            <span className="max-w-[220px] truncate">{s.title}</span>
                            <span className="text-[10px] font-bold text-muted-foreground">{typ}</span>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

          </div>
          <DialogFooter className="flex sm:justify-between gap-2 shrink-0 px-6 py-3.5 bg-secondary/40 border-t border-border">
            <div className="flex gap-2">
              {task && (
                <Button type="button" variant="outline" className="text-destructive border-destructive/30 hover:bg-destructive/10 gap-2" onClick={() => onDelete(task.id)} data-testid="task-delete-button">
                  <Trash2 className="h-4 w-4" /> Delete
                </Button>
              )}
              <Button type="button" variant="outline" className="gap-2" onClick={saveToLibrary} disabled={savingLib || !form.title} data-testid="task-save-to-library-button">
                {savingLib ? <Loader2 className="h-4 w-4 animate-spin" /> : <BookMarked className="h-4 w-4" />} Save to library
              </Button>
            </div>
            <Button type="submit" disabled={saving} className="min-w-[130px]" data-testid="task-save-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save task"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
